import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Link2 } from 'lucide-react';
import i18next from '@/lib/i18n';
import { ProcessCategorySchema, type ProcessCategory } from '@bliks-io/contracts';
import { useEditProcessChain } from '@/api/mutations/useProcessChains';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { FormInput, FormTextarea, FormSelect } from '@/components/form/rhf';
import { getProcessCategoryOptions } from '@/lib/process-utils';

const EditChainFormSchema = z.object({
  name: z.string().min(1, i18next.t('errors.validation.required')),
  description: z.string().optional(),
  category: ProcessCategorySchema
});

type EditChainFormData = z.infer<typeof EditChainFormSchema>;

interface EditChainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chain: {
    _id: string;
    name: string;
    description?: string | null;
    category: ProcessCategory;
  };
}

export function EditChainDialog({ open, onOpenChange, chain }: EditChainDialogProps) {
  const { t } = useTranslation();
  const editChain = useEditProcessChain();

  const defaultValues: EditChainFormData = {
    name: chain.name,
    description: chain.description ?? '',
    category: chain.category
  };

  const handleSubmit = async (data: EditChainFormData) => {
    await editChain.mutateAsync({
      id: chain._id,
      name: data.name.trim(),
      description: data.description?.trim() || null,
      category: data.category
    });
    onOpenChange(false);
  };

  return (
    <FormDialog<EditChainFormData>
      key={chain._id}
      open={open}
      onOpenChange={onOpenChange}
      title={t('processChains.editChain.title')}
      description={t('processChains.editChain.description')}
      icon={Link2}
      schema={EditChainFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isEditing
      size="sm"
    >
      {({ form }) => (
        <div className="flex flex-col gap-4">
          <FormInput
            control={form.control}
            name="name"
            label={t('processChains.createDialog.name')}
            placeholder={t('processChains.createDialog.namePlaceholder')}
            required
            autoFocus
          />
          <FormTextarea
            control={form.control}
            name="description"
            label={t('processChains.createDialog.description')}
            placeholder={t('processChains.createDialog.descriptionPlaceholder')}
            rows={2}
          />
          <FormSelect
            control={form.control}
            name="category"
            label={t('processChains.createDialog.category')}
            options={getProcessCategoryOptions(t)}
            required
          />
        </div>
      )}
    </FormDialog>
  );
}
